import * as React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import { Navigate } from 'react-router-dom'
import VendorWizardSearchCompany from 'vendors/vendorWizard/selectCompanyStep/VendorWizardSearchCompany'
import Ensurement from 'vendors/vendorWizard/selectCompanyStep/Ensurement'
import VendorsPageTitle from 'vendors/components/VendorsPageTitle'
import { useVendorWizard } from 'vendors/vendorWizard/VendorWizardContext'
import { useCheckRepresentationRightsOfCompany } from 'vendors/vendorWizard/selectCompanyStep/queries/useCompaniesQuery'
import LoadingButton from 'proposals/components/LoadingButton'
import { useTranslation } from 'react-i18next'
import AddCompanyDialog from 'proposals/components/AddCompanyDialog'
import RepresentationRightsCheckingView from 'vendors/vendorWizard/RepresentationRightsCheckingView'

const VendorWizardSelectCompanyStep: React.FC<{ baseUrl: string }> = ({ baseUrl }) => {
  const { vendorUser, company, handleCompanyChange, setApprovedRepresentationRightBusinessId } = useVendorWizard()
  const { t } = useTranslation()
  const [isAddCompanyOpen, setIsAddCompanyOpen] = React.useState<boolean>(false)

  const {
    data: isRepresentationRight,
    isSuccess,
    isPending,
    isError,
    mutate: checkRepresentationRights,
    reset
  } = useCheckRepresentationRightsOfCompany()

  const handleCheckRepresentationRightsClick = () => {
    const payload = {
      businessId: company?.businessId ?? '',
      firstName: vendorUser.firstname,
      lastName: vendorUser.lastname,
      phoneNumber: vendorUser.phone
    }

    setApprovedRepresentationRightBusinessId(null)
    checkRepresentationRights(payload, {
      onSuccess: (hasRepresentationRight) => {
        setApprovedRepresentationRightBusinessId(hasRepresentationRight ? payload.businessId : null)
      }
    })
  }

  const toggleAddCompanyDialog = React.useCallback(() => {
    setIsAddCompanyOpen(!isAddCompanyOpen)
  }, [isAddCompanyOpen])

  React.useEffect(() => {
    if (company?.businessId && !company.name && !isAddCompanyOpen) {
      toggleAddCompanyDialog()
    }
  }, [company, isAddCompanyOpen, toggleAddCompanyDialog])

  if (isPending) {
    return <RepresentationRightsCheckingView />
  }

  if (isSuccess && !isRepresentationRight) {
    return <Ensurement onSelectNewCompany={reset} />
  }

  if (isSuccess && isRepresentationRight) {
    // Näytetään success tila josta päästään seuraavaan steppiin. Toistaiseksi siirrytään suoraan seuraavaan steppiin
    return <Navigate to={`${baseUrl}/info`} />
  }

  if (isError) {
    return <Navigate to="/error" />
  }

  return (
    <>
      <Box sx={{ px: 2, pb: 2 }}>
        <VendorsPageTitle>{t('select-company-you-represent')}</VendorsPageTitle>
        <Box sx={{ mt: 3 }}>
          <VendorWizardSearchCompany value={company} onChange={handleCompanyChange} />
          {!company?.businessId ? (
            <Typography variant="body2" sx={{ mt: 3 }}>
              {t('select-represented-company-helper-text-1')}
              <span>
                <Link href="mailto:support@thinger.fi">{t('select-represented-company-helper-text-2')}</Link>
              </span>
              <span>{t('select-represented-company-helper-text-3')}</span>
            </Typography>
          ) : (
            <Typography sx={{ mt: 3 }} variant="body2">
              {t('company-select-success')}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: company?.businessId ? 'flex' : 'none',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <LoadingButton
            sx={{ alignSelf: 'flex-end', mt: 3 }}
            onClick={handleCheckRepresentationRightsClick}
            isLoading={isPending}
          >
            {t('check-representation-right')}
          </LoadingButton>
        </Box>
      </Box>
      <AddCompanyDialog
        open={isAddCompanyOpen}
        onClose={toggleAddCompanyDialog}
        addCompany={handleCompanyChange}
        initialBusinessId={!company?.name ? company?.businessId : ''}
      />
    </>
  )
}

export default VendorWizardSelectCompanyStep
